import React, { useState } from 'react';
import { Button, message, Modal } from 'antd';
import { CheckCircleOutlined, UndoOutlined, WarningFilled } from '@ant-design/icons';
import { green, orange } from '@ant-design/colors';
import moment from 'moment';
import API from '../../../../../API';
import {
	CANCEL_BDO_CARD_CONFIRMATION_COMMAND_TYPE,
	CancelBdoCardConfirmationCommand,
	CONFIRM_BDO_CARD_COMMAND_TYPE,
	ConfirmBdoCardCommand,
} from 'types/commands';
import { OrderType, User, UserLevel } from 'types';
import { useUserLevel } from 'reducers';
import { AsyncButton } from 'components';
import './bdo-card-panel.scss';

interface Props {
	orderType: OrderType.classic | OrderType.loose;
	orderId: number;
	confirmedAt: string | null;
	confirmedBy: User | null;
	onChanged: () => Promise<unknown>;
}

export const BdoCardPanel: React.FC<Props> = ({ orderType, orderId, confirmedAt, confirmedBy, onChanged }) => {
	const userLevel = useUserLevel() ?? UserLevel.driver;
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const [cancelling, setCancelling] = useState(false);

	const handleConfirm = async () => {
		try {
			const command: ConfirmBdoCardCommand = {
				orderId,
				orderType,
			};
			await API.sendCommand(CONFIRM_BDO_CARD_COMMAND_TYPE, command);
			await onChanged();
		} catch (e) {
			message.error('Wystąpił błąd');
			console.trace(e);
		}
	};

	const handleCancel = async () => {
		setCancelling(true);
		try {
			const command: CancelBdoCardConfirmationCommand = {
				orderId,
				orderType,
			};
			await API.sendCommand(CANCEL_BDO_CARD_CONFIRMATION_COMMAND_TYPE, command);
			setCancelDialogOpen(false);
			await onChanged();
		} catch (e) {
			message.error('Wystąpił błąd');
			console.trace(e);
		} finally {
			setCancelling(false);
		}
	};

	if (confirmedAt && confirmedBy) {
		return (
			<div className="flex flex-dir gap-2">
				<span style={{ color: green[5], fontSize: '0.75em' }}>
					<div className="flex flex-dir">
						<span className="flex gap-2">
							<CheckCircleOutlined style={{ color: green[5] }} />
							{moment(confirmedAt).format('DD.MM.YYYY HH:mm')}
						</span>
						<span>
							({confirmedBy.name} {confirmedBy.surname})
						</span>
					</div>
				</span>

				{userLevel >= UserLevel.superAdmin ? (
					<>
						<Button
							type="primary"
							size="large"
							danger
							icon={<UndoOutlined />}
							onClick={() => setCancelDialogOpen(true)}
						>
							Anuluj potwierdzenie
						</Button>

						<Modal
							className="bdo-card-cancel-modal"
							open={cancelDialogOpen}
							onCancel={() => setCancelDialogOpen(false)}
							closable={false}
							centered
							width={420}
							footer={null}
						>
							<WarningFilled style={{ color: '#FAAD14' }} />
							<h1>Czy na pewno chcesz anulować potwierdzenie karty BDO?</h1>
							<h3>Kartę będzie można potwierdzić ponownie</h3>
							<div className="bdo-card-cancel-modal-actions">
								<Button size="large" block onClick={() => setCancelDialogOpen(false)}>
									Nie
								</Button>
								<Button type="primary" size="large" danger block loading={cancelling} onClick={handleCancel}>
									Tak
								</Button>
							</div>
						</Modal>
					</>
				) : null}
			</div>
		);
	}

	if (userLevel >= UserLevel.superAdmin) {
		return <AsyncButton onClick={handleConfirm}>Potwierdź kartę BDO</AsyncButton>;
	}

	return <span style={{ color: orange[5], fontSize: '0.75em' }}>Oczekuje na potwierdzenie karty BDO</span>;
};
